import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from '@tanstack/react-router';
import { useRouteParams } from 'app/router/params';
import { FormEvent, useEffect, useState } from 'react';
import { useCreateLabel, useLabelsByKey, useUpdateLabel } from 'entities/label';
import { dispatchMessage, EventType } from 'shared/feedback/toasts';

interface LabelEntry {
  id: number;
  key: string;
  text: string;
  locale: string;
  visibility: string;
}

interface LabelEditProps {
  isNew?: boolean;
}

const LabelEdit = ({ isNew = false }: LabelEditProps) => {
  const { t } = useTranslation(['common', 'label']);

  const visibilityOptions = [
    { code: 'ANONYMOUS', value: t('label:visibility.ANONYMOUS') },
    { code: 'AUTHENTICATED', value: t('label:visibility.AUTHENTICATED') },
  ];

  const [key, setKey] = useState('');
  const [texts, setTexts] = useState<Record<string, string>>({ sv: '', en: '' });
  const [visibility, setVisibility] = useState(visibilityOptions[1].code);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  const { id } = useRouteParams();
  const navigate = useNavigate();
  const { mutate: createLabel } = useCreateLabel();
  const { mutate: updateLabel } = useUpdateLabel();
  const { data, refetch } = useLabelsByKey(id);

  useEffect(() => {
    if (id) {
      refetch();
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (Array.isArray(data) && data.length > 0) {
      const entries = data as LabelEntry[];
      setKey(entries[0].key);
      setVisibility(entries[0].visibility);
      const texts: Record<string, string> = {};
      entries.forEach((d) => {
        texts[d.locale] = d.text;
      });
      setTexts(texts);
    }
  }, [data]);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setIsSubmitted(true);

    if (key && texts.sv && texts.en) {
      setBusy(true);

      if (isNew) {
        createLabel(
          { key, texts, visibility },
          {
            onSuccess: (data) => {
              if (data) {
                navigate({ to: '/admin/labels/$id', params: { id: key } });
              }
            },
          }
        );

        dispatchMessage(EventType.ADD_MESSAGE, {
          type: 'success',
          title: t('label:create-new.success.title'),
          text: `${t('label:create-new.success.text')} ${key}`,
        });
      } else {
        const updatedLabels = (data as LabelEntry[]).map((d) => ({ ...d, text: texts[d.locale], key }));
        updateLabel(updatedLabels);
        dispatchMessage(EventType.ADD_MESSAGE, {
          type: 'success',
          title: t('label:update.success.title'),
          text: `${t('label:update.success.text')} ${updatedLabels.length ?? 0}`,
        });
      }
    }
  }

  return (
    <>
      <div className="content-title">
        <h1 className="">{t('label:title')}</h1>
        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label className={'col-form-label'} htmlFor="code">
              {t('label:key')}
            </label>
            <input className="form-control" name="key" id="key" type="text" value={key} onChange={(e) => setKey(e.target.value.trim())} />
            {isSubmitted && !key ? <div className="invalid-feedback d-block">Please enter a key.</div> : null}
          </div>

          <div className="mb-3">
            <label className={'col-form-label'} htmlFor="sv">
              {t('label:lang.sv')}
            </label>
            <input className="form-control" type="sv" name="sv" id="sv" value={texts.sv} onChange={(e) => setTexts({ ...texts, sv: e.target.value })} />
            {isSubmitted && !texts.sv ? <div className="invalid-feedback d-block">Please enter a swedish text.</div> : null}
          </div>

          <div className="mb-3">
            <label className={'col-form-label'} htmlFor="en">
              {t('label:lang.en')}
            </label>
            <input className="form-control" type="en" name="en" id="en" value={texts.en} onChange={(e) => setTexts({ ...texts, en: e.target.value })} />
            {isSubmitted && !texts.sv ? <div className="invalid-feedback d-block">Please enter an english text.</div> : null}
          </div>

          <div className="mb-3">
            <div>
              <label className={'col-form-label'} htmlFor="visiblity">
                {t('label:visibility')}
              </label>
            </div>
            <select value={visibility} onChange={(e) => setVisibility(e.target.value)} id="visiblity">
              {visibilityOptions.map((o) => (
                <option key={o.code} value={o.code}>
                  {o.value}
                </option>
              ))}
            </select>
          </div>
          <div className="mt-4 d-flex justify-content-end">
            <Link className="button clear" to="/admin/labels">
              {t('common:cancel')}
            </Link>
            <button className="button light-blue" type="submit" disabled={busy}>
              {t('common:save')}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default LabelEdit;
